import { useState } from "react";
import { Sparkles, X } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";

const TOPICS = [
  { id: "geral", q: "Por que meu preço sugerido ficou assim?" },
  { id: "custo_alto", q: "Por que meu custo por hora está alto?" },
  { id: "reduzir", q: "Como reduzir meu custo operacional?" },
  { id: "peso", q: "O que está pesando mais na minha estrutura?" },
];

export function CostsExplain() {
  const [open, setOpen] = useState(false);
  const [topic, setTopic] = useState("geral");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  const start = async (topicId) => {
    const t = TOPICS.find((x) => x.id === topicId) || TOPICS[0];
    setTopic(t.id);
    setOpen(true);
    setText("");
    setErr(false);
    setLoading(true);
    try {
      const resp = await fetch(`${API}/costs/explain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ topic: t.q }),
      });
      if (!resp.ok || !resp.body) throw new Error("fail");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.error) setErr(true);
            else if (j.t) setText((prev) => prev + j.t);
          } catch { /* chunk parcial */ }
        }
      }
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" className="h-9 border-sky-300 text-sky-700" onClick={() => start("geral")} data-testid="explain-open-btn">
        <Sparkles className="h-4 w-4 mr-1.5" /> Por que esses valores?
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)} data-testid="explain-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-sky-600" /> Assistente de precificação
              </p>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(false)} data-testid="explain-close">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="explain-topics">
              {TOPICS.map((t) => (
                <button key={t.id} type="button" disabled={loading}
                  onClick={() => start(t.id)}
                  className={`text-xs border rounded-full px-2.5 py-1.5 transition-colors ${topic === t.id ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                  data-testid={`explain-chip-${t.id}`}>
                  {t.q}
                </button>
              ))}
            </div>
            {err ? (
              <p className="text-sm text-red-600" data-testid="explain-error">Não foi possível gerar a explicação agora. Tente novamente em instantes.</p>
            ) : (
              <p className={`text-sm text-slate-700 leading-relaxed whitespace-pre-line ${loading && !text ? "animate-pulse" : ""}`} data-testid="explain-text">
                {text || "Gerando explicação personalizada..."}
              </p>
            )}
            <p className="text-[11px] text-slate-400">Resposta gerada por IA com base nos dados cadastrados da sua empresa — é uma análise estimativa, não um diagnóstico contábil. Você decide o preço final.</p>
          </div>
        </div>
      )}
    </>
  );
}
